import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import type { FilterOption } from "@/components/BlotterFilterBar";
import { ImpactFilterMenu } from "@/components/impactReport/ImpactFilterMenu";
import { TopGiftsMenuButton } from "@/components/reports/TopGiftsMenuButton";
import { TOP_GIFTS_TEXT } from "@/constants/reportsTopGifts";
import { campaignButtonLabel, toggleCampaignSelection } from "@/utils/reportsTopGifts-helper";

/**
 * GFG-272 — Reports: Top Gifts. Local to this file: only `TopGiftsCampaignMenu` needs its own
 * props shape, and nothing else crosses a module boundary with it.
 */
interface TopGiftsCampaignMenuProps {
  selected: readonly string[];
  options: readonly FilterOption[];
  isLoading: boolean;
  onChange: (next: string[]) => void;
}

/**
 * The panel's CAMPAIGN control — a multi-select built on the same `ImpactFilterMenu` primitive as
 * the Impact Report's own CAMPAIGN menu (`ImpactControlBar.tsx`), copying its item idiom
 * (font sizes, `Checkbox` sx) but WITHOUT the truncated-list notice: that warning exists there
 * because the options come from a paginated campaign search, while here `options` is the report
 * endpoint's own, already-complete `filterOptions.campaigns`.
 *
 * The menu stays open across clicks — `onChange` is called but `closeMenu` never is — so several
 * campaigns can be bunched onto one report from one open of the menu.
 */
export function TopGiftsCampaignMenu({
  selected,
  options,
  isLoading,
  onChange,
}: TopGiftsCampaignMenuProps) {
  return (
    <ImpactFilterMenu
      isActive={selected.length > 0}
      renderTrigger={({ onOpen, isOpen, triggerAttributes }) => (
        <TopGiftsMenuButton
          label={campaignButtonLabel(selected, options)}
          isActive={selected.length > 0}
          isOpen={isOpen}
          onOpen={onOpen}
          triggerAttributes={triggerAttributes}
          ariaLabel={TOP_GIFTS_TEXT.campaignMenuAria}
        />
      )}
    >
      {() => {
        if (isLoading) {
          return (
            <MenuItem disabled sx={{ fontSize: 13 }}>
              {TOP_GIFTS_TEXT.campaignMenuLoading}
            </MenuItem>
          );
        }

        if (options.length === 0) {
          return (
            <MenuItem disabled sx={{ fontSize: 13, whiteSpace: "normal" }}>
              {TOP_GIFTS_TEXT.campaignMenuEmpty}
            </MenuItem>
          );
        }

        return [
          <MenuItem
            key="__all"
            onClick={() => onChange([])}
            sx={{ fontSize: 13.5, fontWeight: 700, gap: 0.5 }}
          >
            <Checkbox checked={selected.length === 0} size="small" sx={{ p: 0.5, mr: 1 }} />
            {TOP_GIFTS_TEXT.allCampaigns}
          </MenuItem>,
          <Divider key="__divider" sx={{ my: "5px" }} />,
          ...options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onChange(toggleCampaignSelection(selected, option.value))}
              sx={{ fontSize: 13.5, gap: 0.5 }}
            >
              <Checkbox
                checked={selected.includes(option.value)}
                size="small"
                sx={{ p: 0.5, mr: 1 }}
              />
              {option.label}
            </MenuItem>
          )),
        ];
      }}
    </ImpactFilterMenu>
  );
}
